<script setup>
import { computed } from 'vue';

import { HrChip } from '../primitives';

/**
 * Status-action chip — a toggleable chip with an optional rotating chevron for revealing panels or banners.
 *
 * @status recommended
 * @group chips
 * @useWhen Toggling a detail panel, filter section, or banner with a chip that shows its open/closed state
 * @avoidWhen A simple filter toggle is needed — use HrFilterChip
 * @related HrFilterChip
 */
defineOptions({ name: 'HrStatusActionChip' });

const props = defineProps({
  /**
   * Chip label text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Active
   */
  label: {
    type: String,
    default: null,
  },
  /** Expanded/selected state via v-model. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Material icon name shown before the label.
   * @example info
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Chip size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a skeleton placeholder instead of the chip. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Shows a trailing chevron that rotates to indicate the open/closed state
   * of a panel or banner revealed by this chip.
   */
  chevron: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the chip is toggled; payload is the new boolean value. */
  'update:modelValue': null,
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = computed(() => (
  props.modelValue
    ? { color: 'secondary', textColor: 'white' }
    // NO `color` when unselected. On an outlined Quasar chip `color` paints the LABEL and the
    // border, not the fill, so `color: 'white'` rendered white text and a white outline on a white
    // surface — an empty pill. Every sibling chip passes a bare `outline` here for that reason.
    : { outline: true }
));

const chevronIcon = computed(() => {
  if (!props.chevron) return null;
  return props.modelValue ? 'expand_less' : 'expand_more';
});
</script>

<template>
  <hr-chip
    variant="status-action"
    clickable
    :label="label"
    :icon="icon"
    :icon-right="chevronIcon"
    :size="size"
    :selected="modelValue"
    :disable="disable"
    :pending="pending"
    :legacy-props="legacyProps"
    v-bind="$attrs"
    @click="emit('update:modelValue', !modelValue)"
  >
    <!-- @slot default Custom chip label content; use instead of the `label` prop for rich content. -->
    <slot />
  </hr-chip>
</template>
